import { maskValue } from '../../../../helpers/maskValue/maskValue';
import { useLocale } from '../../../../context/Localization/localization';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { ChangeEvent, memo, useEffect, useRef, FocusEvent } from 'react';
import { useField, useFormikContext } from 'formik';
import { WebFormVinFieldProps } from '../FormVinField.types';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const WebFormVinField = memo(
    ({
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        initiallyMasked,
        tabFocusRefContext,
        onChangeCallback,
        vinType,
    }: WebFormVinFieldProps) => {
        const [field, meta] = useField<string>({
            name: name,
            validate: TextFieldValidation({
                required: required ?? false,
                label,
                name,
                validationType: vinType,
            }),
        });
        const { submitCount, setFieldValue } = useFormikContext();

        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);
        /**
         * The focus ref to prevent a stale state when the on-screen keyboard
         * calls any functions that need to know if the field is focused or not.
         */
        const isFocused = useRef<boolean>(false);
        /**
         * The input value ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal value state.
         *
         * This should be handled by react-imask
         */
        const valueRef = useRef<string>(initiallyMasked && !isFocused.current ? maskValue(field.value) : field.value ?? '');
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        const {
            dealer: { primaryLanguage },
        } = useKioskState();
        const locale = useLocale();

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        const handleOnBlur = () => {
            isFocused.current = false;
        };

        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            valueRef.current = event.target.value;
            setFieldValue(name, event.target.value);
            onChangeCallback && onChangeCallback(event);
        };

        const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            /**
             * This handles unmasking the vin when the vin is masked.
             */
            if (valueRef.current.indexOf('*') !== -1) {
                valueRef.current = '';
                setFieldValue(name, '');
            }
            isFocused.current = true;
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'text'}
                type={'text'}
                inputRef={inputRef}
                value={valueRef.current}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onFocus={handleOnFocus}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
                inputProps={{
                    maxLength: 17,
                }}
            />
        );
    });
